/**
 * Seconds a magic link stays valid for once sent, mirrored from the backend rather than read
 * from the link itself: the frontend never sees the real `exp` claim, so `LinkSent`'s callers
 * compute an expiry moment client-side from the moment they fire the send. Three independently
 * maintained copies of this number exist:
 *
 * 1. the backend default,
 *    `cdk/passwordless-auth/lambda/verifime-create-auth-challenge/configureMagicLink.ts:26`
 *    (`selfSignUpLinkSecondsUntilExpiry`, defaulting to `60 * 15`);
 * 2. the rendered phrase `'15 minutes'` in that same lambda's `notificationPayload.ts`
 *    `EXPIRY_TEXT` map, whose own header comment already warns it drifts independently of the
 *    seconds value above;
 * 3. this constant.
 *
 * There is no runtime check tying any of the three together, so a change on one side goes
 * unnoticed on the others: the same unreachable-mirror trade-off `ExpiryClock.tsx`'s own TD-034
 * comment documents for its icon size.
 */
export const MAGIC_LINK_LIFETIME_SECONDS = 60 * 15;

/**
 * Given the moment a magic link was sent, answers the moment it stops working: `sentAt` plus
 * `MAGIC_LINK_LIFETIME_SECONDS`. Pure and co-located with `LinkSent` (ADR-104). `LinkSent` itself
 * still takes `expiresAt` as a prop and never calls this: a caller computes it, typically at the
 * same moment it fires the send request, and passes the result down.
 *
 * An invalid `sentAt` (for example `new Date('nonsense')`) propagates to an invalid result
 * rather than throwing: `NaN` runs through the addition unchanged, so the returned `Date` is
 * itself invalid. This is deliberate, not an oversight: it matches how `formatExpiryTime`
 * already fails soft on an unparseable value, and it is absorbed two hops downstream, at
 * `ExpiryClock`, which renders nothing at all when it cannot parse the expiry it is given.
 */
export function computeLinkExpiry(sentAt: Date): Date {
  return new Date(sentAt.getTime() + MAGIC_LINK_LIFETIME_SECONDS * 1000);
}
